import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import { TextField, FormFooterActions } from "../../components/admin/FormFields";
import { KeyRound } from "lucide-react";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  // Change password state
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const changePassword = async () => {
    if (!pwd.current || !pwd.next || !pwd.confirm) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (pwd.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      // Server rotates the httpOnly auth cookie, so the admin stays logged in
      toast.success("Password updated successfully");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password change failed");
    } finally {
      setPwdSaving(false);
    }
  };

  if (!s) return <div className="text-[#D8D6D0]/40">Loading…</div>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <AdminHeader
        title="Site Settings"
        subtitle="Business info used in the footer, contact pages, and JSON-LD structured data (Google rich snippets)."
      />

      <div className="bg-[#151515] border border-[#D8D6D0]/10 p-6 md:p-8">
        <div className="overline text-[#8C1D30] mb-5">Business</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <TextField id="business_name" label="Business Name" value={s.business_name} onChange={(v) => set("business_name", v)} required />
          <TextField id="tagline" label="Tagline" value={s.tagline} onChange={(v) => set("tagline", v)} />
          <TextField id="founded_year" label="Founded Year" type="number" value={s.founded_year} onChange={(v) => set("founded_year", v)} />
          <TextField id="projects_total" label="Total Projects" type="number" value={s.projects_total} onChange={(v) => set("projects_total", v)} />
          <div className="md:col-span-2">
            <TextField id="site_base_url" label="Site Base URL" value={s.site_base_url} onChange={(v) => set("site_base_url", v)} hint="Production domain — used in sitemap.xml and JSON-LD. Update after deploying to your custom domain. e.g. https://northcrestlandscaping.ca" required />
          </div>
        </div>

        <div className="overline text-[#8C1D30] mt-8 mb-5">Contact</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <TextField id="phone" label="Phone (E.164)" value={s.phone} onChange={(v) => set("phone", v)} hint="Used in tel: links. e.g. +1-780-655-7604" />
          <TextField id="phone_display" label="Phone (Display)" value={s.phone_display} onChange={(v) => set("phone_display", v)} hint="Shown to humans. e.g. (780) 655-7604" />
          <TextField id="email" label="Contact Email" type="email" value={s.email} onChange={(v) => set("email", v)} />
          <TextField id="address_locality" label="City" value={s.address_locality} onChange={(v) => set("address_locality", v)} />
          <TextField id="address_region" label="Region" value={s.address_region} onChange={(v) => set("address_region", v)} hint="Two-letter province code (e.g. AB)" />
          <TextField id="address_country" label="Country" value={s.address_country} onChange={(v) => set("address_country", v)} hint="Two-letter ISO (e.g. CA)" />
        </div>

        <div className="overline text-[#8C1D30] mt-8 mb-5">Social & Reviews</div>
        <div className="grid grid-cols-1 gap-x-6">
          <TextField id="instagram_url" label="Instagram URL" value={s.instagram_url} onChange={(v) => set("instagram_url", v)} />
          <TextField id="facebook_url" label="Facebook URL" value={s.facebook_url} onChange={(v) => set("facebook_url", v)} />
          <TextField id="google_reviews_url" label="Google Business Profile URL" value={s.google_reviews_url} onChange={(v) => set("google_reviews_url", v)} hint='Used for the "Leave a Review" button on Home and About pages. Get from your Google Business Profile → "Share review form" (e.g. https://g.page/r/CXXXXXX/review)' />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <TextField id="rating_value" label="Google Rating" value={s.rating_value} onChange={(v) => set("rating_value", v)} hint="e.g. 4.9" />
          <TextField id="rating_count" label="Review Count" type="number" value={s.rating_count} onChange={(v) => set("rating_count", v)} />
        </div>

        <div className="mt-8 pt-6 border-t border-[#D8D6D0]/10">
          <FormFooterActions onCancel={() => window.location.reload()} onSubmit={save} submitting={saving} submitLabel="Save Settings" />
        </div>

        <p className="text-[11px] text-[#D8D6D0]/40 mt-6">
          Changes here update the footer, contact page, and JSON-LD schema markup site-wide. SEO impact may take 1–2 weeks to reflect in Google search results.
        </p>
      </div>

      {/* Security — Change Password */}
      <div className="bg-[#151515] border border-[#D8D6D0]/10 p-6 md:p-8 mt-8" data-testid="security-section">
        <div className="flex items-center gap-3 mb-5">
          <KeyRound className="w-4 h-4 text-[#8C1D30]" />
          <div className="overline text-[#8C1D30]">Security</div>
        </div>
        <h3 className="font-serif-display text-2xl mb-2">Change admin password</h3>
        <p className="text-xs text-[#D8D6D0]/55 mb-6 max-w-lg">
          Choose a strong password with at least 8 characters. You&apos;ll stay logged in after changing.
        </p>

        <div className="max-w-md">
          <TextField
            id="current-password"
            label="Current Password"
            type="password"
            value={pwd.current}
            onChange={(v) => setPwd({ ...pwd, current: v })}
            required
            testId="current-password"
          />
          <TextField
            id="new-password"
            label="New Password"
            type="password"
            value={pwd.next}
            onChange={(v) => setPwd({ ...pwd, next: v })}
            required
            hint="Minimum 8 characters."
            testId="new-password"
          />
          <TextField
            id="confirm-password"
            label="Confirm New Password"
            type="password"
            value={pwd.confirm}
            onChange={(v) => setPwd({ ...pwd, confirm: v })}
            required
            testId="confirm-password"
          />

          <div className="mt-6 pt-6 border-t border-[#D8D6D0]/10">
            <FormFooterActions
              onCancel={() => setPwd({ current: "", next: "", confirm: "" })}
              onSubmit={changePassword}
              submitting={pwdSaving}
              submitLabel="Change Password"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
